import { useAppDispatch } from "app/hooks";
import LoginPage, { ILoginFormData } from "components/pages/LoginPage";
import { loginAsync } from "adminPanel/user/userSlice";
import useTranslate from "hooks/useTranslate";
import { useNavigate, useSearchParams } from "react-router";
import { useContext, useEffect, useRef, useState } from "react";
import MessageDialogContext from "context/MessageDialogContext";
import { Stack, CircularProgress } from "@mui/material";
import { ILoginError, LoginErrorStatus } from "./models/LoginErrorStatus";
import userApi from "./userApi";
import { Snackbar } from "components/general/Snackbar";
import { POSType } from "amnPos/pages/UserHomePage";
import EmailVerifiedSuccessDialog from "./EmailVerifiedSuccessDialog";
import useStoreDetailsIssueDialog from "adminPanel/stores/useStoreDetailsIssueDialog";
import { handleStoreDetailsIssueError } from "adminPanel/stores/isStoreDetailsIssueError";
import { TaxesSystems } from "constants/taxesSystems";
import EndOtherSessionDialog from "components/general/EndOtherSessionDialog";

export default function UserLoginPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const messageDialog = useContext(MessageDialogContext);
  const { translate } = useTranslate("admin.user");
  const { translate: taxTranslate } = useTranslate("admin.taxes");
  const [error, setError] = useState<ILoginError>();
  const [searchParams, setSearchParams] = useSearchParams();
  const showEmailVerifiedDialog = searchParams.get("emailVerified") === "1";
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const emailVerificationStarted = useRef(false);

  const [openConfirmationDialog, setOpenConfirmationDialog] =
    useState<boolean>(false);
  const [handleOnConfirm, setHandleOnConfirm] = useState<() => void>();
  const showStoreDetailsIssue = useStoreDetailsIssueDialog();

  useEffect(() => {
    const verifyCode = searchParams.get("verifyCode");
    if (!verifyCode || emailVerificationStarted.current) return;

    emailVerificationStarted.current = true;
    setIsVerifyingEmail(true);

    userApi
      .verifyOrganizationEmail(verifyCode)
      .then((response) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete("verifyCode");
        nextParams.delete("orgIdentifier");
        nextParams.delete("verifyType");

        if (response.data.status === 1 || response.data.status === 2) {
          nextParams.set("emailVerified", "1");
          setSearchParams(nextParams, { replace: true });
          return;
        }

        setError({
          status: LoginErrorStatus.EmailIsNotVerified,
          error: {
            name: "errorLogin",
            value: response.data.message,
          },
        });
        setSearchParams(nextParams, { replace: true });
      })
      .catch((e) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete("verifyCode");
        nextParams.delete("orgIdentifier");
        nextParams.delete("verifyType");
        setSearchParams(nextParams, { replace: true });
        setError({
          status: LoginErrorStatus.EmailIsNotVerified,
          error: {
            name: "errorLogin",
            value:
              e.response?.data?.errors?.mainErrors?.[0] ||
              translate("errorOccurContactWithAdmin"),
          },
        });
      })
      .finally(() => setIsVerifyingEmail(false));
  }, [searchParams, setSearchParams, translate]);

  function closeEmailVerifiedDialog() {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("emailVerified");
    setSearchParams(nextParams, { replace: true });
  }
  function Login(data: any, useCookie: boolean, forceLogin: boolean) {
    dispatch(
      loginAsync({
        data: {
          ...data,
          useCookie: useCookie,
          forceLogin: forceLogin,
        },
        onSuccess: (
          organizationId?: string,
          isCasher?: boolean,
          posId?: string,
          PosUserId?: string,
          posType?: POSType,
        ) => {
          Snackbar(translate("loginSuccess"), "success");
          if (isCasher) {
            if (posId != null) {
              localStorage.setItem("posId", posId ?? "");
              localStorage.setItem(
                "posType",
                (posType ?? POSType.POS).toString(),
              );
              if (posType == POSType.Restaurants) {
                navigate(`/${organizationId}/restaurants`);
              } else {
                navigate(`/${organizationId}/pos`);
              }
            } else {
              navigate(`/${organizationId}/home`);
            }
          } else {
            navigate(`/${organizationId}`);
          }
        },
        onError: (e) => {
          if (e.response.status === 409) {
            setOpenConfirmationDialog(true);
            setHandleOnConfirm(() => () => {
              Login(data, true, true);
              setOpenConfirmationDialog(false);
            });
            return;
          }
          if (handleStoreDetailsIssueError(e.response, showStoreDetailsIssue)) {
            return;
          }
          if (e.response.status === 488) {
            messageDialog.setActionCloseMessageInfo(
              translate("posNotLinkedToDeviceHeaderMessage"),
              translate("posNotLinkedToDeviceMessage"),
              "close",
            );
          } else if (e.response.status === 489) {
            messageDialog.setActionCloseMessageInfo(
              taxTranslate("storeNotLinkedToStationTitle"),
              taxTranslate("storeNotLinkedToStationBody"),
              "close",
            );
          } else if (e.response.status === 477) {
            messageDialog.setActionCloseMessageInfo(
              taxTranslate("businessInfoRequiredTitle"),
              taxTranslate("businessInfoRequiredBody").replace(
                "{{taxSystem}}",
                taxTranslate(
                  TaxesSystems.find(
                    (c) =>
                      c.value ===
                      Number(e.response.data.errors["TaxSystem"][0]),
                  )!.name,
                ),
              ),
              "close",
            );
          }
          else if (e.response.status === 478) {
             messageDialog.setActionCloseMessageInfo(
              taxTranslate("employeeNotActiveTitle"),
              taxTranslate("employeeNotActiveBody"),
              "close",
            );
          } else {
            setError({
              status: e.response.data.status,
              error: {
                name: "errorLogin",
                value:
                  e.response.data.errors.User?.[0] ??
                  e.response.data.errors.mainErrors,
              },
            });
          }
        },
      }),
    );
  }
  function submitHandler(data: ILoginFormData) {
    Login(data, true, false);
  }

  function resendVerifyEmail() {
    userApi
      .resendVerify()
      .then(() => {
        Snackbar(translate("emailSent"), "success");
        setError(undefined);
      })
      .catch((e) =>
        setError({
          status: LoginErrorStatus.EmailIsNotVerified,
          error: {
            name: "resendEmail",
            value:
              e.response?.data?.errors?.mainErrors?.[0] ||
              translate("errorOccurContactWithAdmin"),
          },
        }),
      );
  }

  return (
    <Stack sx={{ position: "relative" }}>
      {isVerifyingEmail && (
        <Stack
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1400,
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(255,255,255,0.65)",
          }}
        >
          <CircularProgress color="info" />
        </Stack>
      )}
      <EndOtherSessionDialog
        open={openConfirmationDialog}
        onCancel={() => {
          setOpenConfirmationDialog(false);
        }}
        onConfirm={handleOnConfirm}
        title={translate("endOtherSessionTitle")}
        message={translate("endOtherSessionMessage")}
        confirmButtonText={translate("endOtherSession")}
        cancelButtonText={translate("cancel")}
      />
      <EmailVerifiedSuccessDialog
        open={showEmailVerifiedDialog}
        onClose={closeEmailVerifiedDialog}
      />
      <LoginPage
        submitHandler={submitHandler}
        resendEmail={resendVerifyEmail}
        error={error}
        data-testid="user-login-page"
      />
    </Stack>
  );
}
